import { onMessage } from "firebase/messaging";
import Cookies from "js-cookie";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../api";
import { messaging, registerToken } from "../api/fcm";
import {
  authEvents$,
  chatEvents$,
  closeAuthSocket,
  closeChatSocket,
  initAuthSocket,
  initChatSocket,
  SOCKET_STATUS,
  socketStatus$,
} from "../api/webSocketRx";
import { conversationType, MESSAGE_STATUS } from "../config/Constant";
import { useCalling } from "../store/CallingStore";
import { useInfoUser } from "../store/UserStore";
import {
  openNotificationError,
  openNotificationWarn,
  verifiedRefreshToken,
} from "../utils/Utils";
import { pushNotification } from "../utils/UtilsNotification";

const AuthLayoutContext = createContext(null);
const REACT_APP_AUTHENTICATION_WEB =
  process.env.REACT_APP_AUTHENTICATION_WEB || "http://localhost:4005";

export const useAuthLayoutContext = () => useContext(AuthLayoutContext);

export const AuthLayoutProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    user,
    updateUser,
    updateLanguageMap,
    languageMap,
    updateStickerKeywords,
  } = useInfoUser();

  const { setIsVideo } = useCalling();
  const [orgList, setOrgList] = useState([]);
  const [isTreeLoading, setIsTreeLoading] = useState(false);

  const [verified, setVerified] = useState(false);

  const [friend, setFriend] = useState(null);
  const [callingId, setCallingId] = useState(null);

  const [connectionState, setConnectionState] = useState({
    status: SOCKET_STATUS.DISCONNECTED,
    retryCount: 0,
  });

  const getMe = async () => {
    if (user) return;

    const me = await apiFactory.userApi.getMe();
    if (me?.status === 200) {
      updateUser(me?.data?.user);
      updateLanguageMap(me?.data?.languageMap);
      updateStickerKeywords(me?.data?.stickerKeywords);
      await registerToken(me?.data?.user?.userId);
    } else {
      toast.error(me?.message);
      updateUser(null);
      updateLanguageMap(null);
      Cookies.remove("access_token");
      navigate("/login");
    }
  };

  const fetchOrganizations = async () => {
    setIsTreeLoading(true);
    const result = await apiFactory.organizationApi.search({ setting: true });

    if (result?.status !== 200) {
      openNotificationError(result?.message);
      return;
    }

    const orgs = result?.data?.map((org) => ({
      ...org,
      checked: false,
      indeterminate: false,
      fetched: true,
      isOpen: false,
      id: uuidv4(),
    }));

    //auto open root node
    for (let org of orgs) {
      if (org?.parentId) continue;
      org.isOpen = true;
    }

    setOrgList([...orgs]);
    setIsTreeLoading(false);
  };

  const getAccessToken = async () => {
    if (user) return;

    const result = await apiFactory.authApi.getAccessToken({
      refreshToken: Cookies.get("refresh_token"),
    });
    if (result?.status === 200) {
      Cookies.set("access_token", result?.data);
    }
  };

  const process = async () => {
    const redirectRefreshToken = searchParams.get("token");
    let isValidToken = false;

    if (redirectRefreshToken) {
      Cookies.set("refresh_token", redirectRefreshToken);
      isValidToken = true;
    } else {
      isValidToken = verifiedRefreshToken();
    }

    setVerified(isValidToken);

    if (!isValidToken) {
      closeChatSocket();
      closeAuthSocket();
      window.location.href =
        REACT_APP_AUTHENTICATION_WEB +
        "/login?callback=" +
        window.location.href;
      return;
    }

    await getAccessToken();

    initChatSocket();
    initAuthSocket();

    await getMe();

    if (location?.pathname === "/") {
      navigate("/conversation");
    } else if (redirectRefreshToken) {
      let finalRedirectUrl = window.location.href.replace(
        "?token=" + redirectRefreshToken,
        "",
      );

      finalRedirectUrl = finalRedirectUrl.replace(
        "&&token=" + redirectRefreshToken,
        "",
      );

      window.location.href = finalRedirectUrl;
    }

    await fetchOrganizations();
  };

  const processFirebase = () => {
    try {
      onMessage(messaging, (payload) => {
        const data = {
          announcer:
            payload?.data?.announcer && JSON.parse(payload?.data?.announcer),
          basicConversationInfo:
            payload?.data?.basicConversationInfo &&
            JSON.parse(payload?.data?.basicConversationInfo),
          message: payload?.data?.message && JSON.parse(payload?.data?.message),
          type: payload?.data?.type,
        };

        const message = data?.message;
        const isSendImage = message?.contentType === MESSAGE_STATUS.IMAGE;
        const isSendText = message?.contentType === MESSAGE_STATUS.TEXT;
        const isSendFile = message?.contentType === MESSAGE_STATUS.FILE;

        const isShareMessage = data?.type === MESSAGE_STATUS.SHARE_MESSAGE;
        const isReactionMessage =
          data?.type === MESSAGE_STATUS.REACTION_MESSAGE;

        const isPinnedMessage =
          message?.contentType === MESSAGE_STATUS.PINNED_MESSAGE;
        const isUnPinnedMessage =
          message?.contentType === MESSAGE_STATUS.UNPINNED_MESSAGE;
        const isPromotedMessage =
          message?.contentType === MESSAGE_STATUS.PROMOTED_MESSAGE;

        const isSendByMe = data?.announcer?.userId === user?.userId;

        if (!isSendByMe) {
          handleNotifications(
            data,
            isSendText,
            isSendFile,
            isSendImage,
            isShareMessage,
            isReactionMessage,
            isUnPinnedMessage,
            isPinnedMessage,
            isPromotedMessage,
          );
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleNotifications = (
    data,
    isSendText,
    isSendFile,
    isSendImage,
    isShareMessage,
    isReactionMessage,
    isUnPinnedMessage,
    isPinnedMessage,
    isPromotedMessage,
  ) => {
    const userName = data?.announcer?.userName;

    if (isReactionMessage) {
      if (
        data?.message?.senderId === user?.userId &&
        Object.values(data?.message?.respondent || {})?.[0] !== "CANCEL"
      ) {
        pushNotification(
          `${userName} ${
            languageMap?.["message.event.reaction"] ?? "has send a reaction"
          }`,
          data?.message?.conversationId || null,
          languageMap,
        );
      }
    } else if (isShareMessage) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.shareMessage"] ?? "has shared a message."
        }`,
        data?.basicConversationInfos?.[0]?.conversationId || null,
        languageMap,
      );
    } else if (isUnPinnedMessage) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.isUnPinnedMessage"] ??
          "has unpinned a message."
        }`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    } else if (isPinnedMessage) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.isPinnedMessage"] ??
          "has pinned a message."
        }`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    } else if (isPromotedMessage) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.isPromotedMessage"] ??
          "has promoted a message."
        }`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    } else if (
      data?.basicConversationInfo?.type === conversationType.NOTIFICATION
    ) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.sendMessageNotification"] ??
          "sent you a message notification"
        }`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    } else if (isSendText) {
      switch (data?.basicConversationInfo?.type) {
        case conversationType.GROUP:
          return pushNotification(
            `${userName} ${
              languageMap?.["message.event.sendMessageConversation"] ??
              "has send a message conversation"
            }`,
            data?.basicConversationInfo?.conversationId || null,
            languageMap,
          );
        default:
          return pushNotification(
            `${userName} ${
              languageMap?.["message.event.sendMessage"] ??
              "sent you the message"
            }`,
            data?.basicConversationInfo?.conversationId || null,
            languageMap,
          );
      }
    } else if (isSendFile) {
      pushNotification(
        `${userName} ${languageMap?.["message.event.sendFile"] ?? "Attachments"}`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    } else if (isSendImage) {
      pushNotification(
        `${userName} ${
          languageMap?.["message.event.sendImage"] ?? "sent you a photo"
        }`,
        data?.basicConversationInfo?.conversationId || null,
        languageMap,
      );
    }
  };

  const rejectCall = () => {
    setFriend(null);
    setIsVideo(false);
  };

  useEffect(() => {
    process();
  }, []);

  useEffect(() => {
    const sub = socketStatus$.subscribe((s) => {
      if (s?.scope === "chat") {
        setConnectionState({
          status: s.status,
          retryCount: s.retryCount ?? 0,
        });
      }
    });

    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    const sub = chatEvents$.subscribe((data) => {
      if (!data) return;

      switch (data?.event) {
        case "session-uuid":
          sessionStorage.setItem("sessionUuid", data?.sessionUuid);
          break;
        case "request":
          setFriend(data?.from);
          setCallingId(data?.callingId);
          setIsVideo(true);
          break;
        case "accept":
          setIsVideo(false);
          break;
        case "busy":
          openNotificationWarn("Your friend is in other call !");
          break;
        case "end":
          setIsVideo(false);
          break;
        default:
          break;
      }
    });

    return () => sub.unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const sub = authEvents$.subscribe(async (data) => {
      if (!data) return;

      if (data?.type === "UPDATE_ORGANIZATION") {
        await fetchOrganizations();
      }
    });

    return () => sub.unsubscribe();
  }, [user]);

  useEffect(() => {
    if (user) processFirebase();
  }, [user]);

  useEffect(() => {
    window.onbeforeunload = function () {
      closeChatSocket();
      closeAuthSocket();
    };
  }, []);

  const values = useMemo(
    () => ({
      verified,
      setVerified,
      friend,
      rejectCall,
      callingId,
      connectionState,
      orgList,
      setOrgList,
      fetchOrganizations,
      isTreeLoading,
    }),
    [verified, friend, callingId, connectionState, orgList, isTreeLoading],
  );

  return (
    <AuthLayoutContext.Provider value={values}>
      {children}
    </AuthLayoutContext.Provider>
  );
};
